import React, {FC, useEffect, useMemo} from 'react';

import {byTerm, searchTermIsSport, selectMatches} from 'utils/matchFns';
import {DateTabsType, Sport} from 'utils/types';
import {ReactTitle} from 'react-meta-tags';
// components
import Ad from 'components/Common/Ad';
import SearchBar from 'components/Matchcenter/SearchBar';
import DateTabs from 'components/Matchcenter/DateTabs';
import TabbedMatchLists from 'components/Matchcenter/TabbedMatchLists';
import WidgetTVProgram from 'components/Matchcenter/WidgetTVProgram';
import WidgetTeaser from 'components/Matchcenter/WidgetTeaser';
// hooks
import useMatches from 'hooks/useMatches';
import useCompetitions from 'hooks/useCompetitions';
import usePolling from 'hooks/usePolling';
import useSearchTerm from 'hooks/useSearchTerm';
import useDateTabs from 'hooks/useDateTabs';
import useTeaserArticles from 'hooks/useTeaserArticles';
import useTVProgramEvents from 'hooks/useTVProgramEvents';
import useBreakpointXL from 'hooks/useBreakpointXL';
import useBreakpointLG from 'hooks/useBreakpointLG';
import {dateTabsTotal} from 'store/dateTabs/utils';

import getConfig from '../../getConfig';

import './styles.scss';

const {pollingRates} = getConfig();

// TODO: fallback if URL invalid
// TODO: skeleton loader

/**
 * Displays the list of available matches grouping them by day or month.
 * Handles the business logic of fetching (and polling) the data when the 'searchTerm' changes,
 * it can be 'month' or 'days'
 *
 * Live matches have a different polling rate compared to matches in the past, to handle this the
 * business logic of fetching/polling the data has been split into smaller components via
 * the AsyncMatchListItem component, which is itself connected to the redux store via custom hooks.
 */
const MatchcenterPage: FC = () => {
  const isBreakpointLG = useBreakpointLG();
  const isBreakpointXL = useBreakpointXL();

  const [matchesState, matchesActions] = useMatches();
  const [competitionsState, {getCompetitions}] = useCompetitions();

  const [searchTerm, searchTermName, setSearchTerm] = useSearchTerm(competitionsState.data);

  const [
    {tabsType: dateTabsType, tabs: dateTabs, selectedTabIndex: dateTabIndex},
    {setDateTabsType, setSelectedDateTab},
  ] = useDateTabs();
  const currentDate = useMemo(() => dateTabs[dateTabIndex].date, [dateTabs, dateTabIndex]);

  // BIQ: maybe move this into useDateTabs hook?
  /**
   * When user has searched, transform tabs
   */
  useEffect(() => {
    let type = DateTabsType.DAYS;
    if (searchTerm) type = DateTabsType.MONTHS;

    setDateTabsType(type);

    if (!dateTabIndex || dateTabIndex >= dateTabsTotal[type]) setSelectedDateTab();
  }, [searchTerm, dateTabIndex, setDateTabsType, setSelectedDateTab]);

  const {withPolling, clearAllPollings} = usePolling();

  const matchesInDates = useMemo(
    () => selectMatches(searchTerm, currentDate, matchesState?.data, competitionsState?.data),
    [searchTerm, currentDate, matchesState, competitionsState]
  );

  const competition = !searchTermIsSport(searchTerm)
    ? competitionsState?.data?.find(byTerm(searchTerm))
    : null;

  // polling versions of the matches-related actions
  const getMatchesByDate = withPolling(matchesActions.getMatchesByDate, pollingRates.sportEvents);
  const getMatchesBySportAndMonth = withPolling(
    matchesActions.getMatchesBySportAndMonth,
    pollingRates.sportEvents
  );
  const getMatchesByCompetitionIdAndMonth = withPolling(
    matchesActions.getMatchesByCompetitionIdAndMonth,
    pollingRates.sportEvents
  );

  const [teaserArticlesState, {getTeaserArticles}] = useTeaserArticles();
  const {loading: teaserArticleLoading, data: teaserArticles} = teaserArticlesState;

  const [tvProgramEventsState, {getTVProgramEvents}] = useTVProgramEvents();
  const {loading: tvProgramEventsLoading, data: tvProgramEvents} = tvProgramEventsState;

  /**
   * Article feeds should only be fetched once per component mount. No cleanup necessary.
   */
  useEffect(() => {
    const now = new Date();

    getTVProgramEvents(now);
    getTeaserArticles();
  }, []);

  /**
   * The following effect wraps up the business logic of choosing which polling function should
   * be performed, according to the user selection and the inputted search terms
   */
  useEffect(() => {
    if (dateTabsType === DateTabsType.DAYS) {
      getMatchesByDate(currentDate);
    }

    if (searchTermIsSport(searchTerm)) {
      getMatchesBySportAndMonth(searchTerm as Sport, currentDate);
    }

    if (competition) {
      getMatchesByCompetitionIdAndMonth(competition.sportRadarId, currentDate);
    }

    return () => {
      getMatchesByDate.cancel();
      getMatchesBySportAndMonth.cancel();
      getMatchesByCompetitionIdAndMonth.cancel();
    };
  }, [
    dateTabsType,
    searchTerm,
    currentDate,
    competition,
    getMatchesByDate,
    getMatchesBySportAndMonth,
    getMatchesByCompetitionIdAndMonth,
  ]);

  /**
   * When the component mounts fetch all the competitions data, if not preset yet
   */
  useEffect(() => {
    if (!competitionsState.data && !competitionsState.loading && !competitionsState.error) {
      getCompetitions();
    }
  }, [getCompetitions, competitionsState]);

  /**
   * When component un-mounts clears all the generated polling functions
   */
  useEffect(() => clearAllPollings, [clearAllPollings]);

  return (
    <div className="MatchcenterPage">
      <ReactTitle
        title={
          searchTerm
            ? `${searchTermName} live στο Matchcenter | SPORT 24`
            : 'Όλοι αγώνες live στο Matchcenter | SPORT24'
        }
      />
      <header className="MatchcenterPage__header">
        <SearchBar
          term={searchTerm}
          competitions={competitionsState.data}
          onChange={value => setSearchTerm(value)}
        />
        <DateTabs
          searchTerm={searchTerm}
          dateTabs={dateTabs}
          tab={dateTabIndex}
          setTab={setSelectedDateTab}
          isBreakpointLG={isBreakpointLG}
        />
      </header>
      <main className="MatchcenterPage__main">
        {isBreakpointXL && (
          <section className="MatchcenterPage__right">
            <WidgetTVProgram loading={tvProgramEventsLoading} tvData={tvProgramEvents} />
            <Ad
              adUnit="sport24.gr/Matchcenter_hp_300x250A"
              scope="matchcenterpage"
              sizes={[[300, 250]]}
              sizeMapping={[{viewport: [1024, 0], sizes: [[300, 250]]}]}
            />
            <WidgetTeaser loading={teaserArticleLoading} articles={teaserArticles} />
            <Ad
              adUnit="sport24.gr/Matchcenter_hp_300x250C"
              scope="matchcenterpage"
              sizes={[
                [300, 600],
                [300, 250],
              ]}
              sizeMapping={[
                {
                  viewport: [1024, 0],
                  sizes: [
                    [300, 600],
                    [300, 250],
                  ],
                },
              ]}
              sticky
              autoRefresh
            />
          </section>
        )}
        <section className="MatchcenterPage__center">
          <TabbedMatchLists
            loading={matchesState.loading}
            search={searchTerm}
            matchesInDates={matchesInDates}
            dateTab={dateTabIndex}
            competitions={competitionsState.data}
            isBreakpointLG={isBreakpointLG}
          />
        </section>
        {isBreakpointLG && (
          /* FIXME: unmount this and destroy slot gracefully - maybe also remove sizemapping */
          <section className="MatchcenterPage__right">
            <Ad
              adUnit="sport24.gr/Matchcenter_hp_300x250B"
              scope="matchcenterpage"
              sizes={[
                [300, 600],
                [300, 250],
              ]}
              sizeMapping={[
                {
                  viewport: [1024, 0],
                  sizes: [
                    [300, 600],
                    [300, 250],
                  ],
                },
              ]}
              sticky
              autoRefresh
            />
          </section>
        )}
      </main>
      {/* !isBreakpointLG && (
        // FIXME: umnount his and destroy slot gracefully OR mount and setup 
        <Ad
          adUnit="sport24.gr/Matchcenter_hp_300x50stuck"
          scope="matchcenterpage"
          sizes={[[320, 50]]}
          stuck
          autoRefresh
        />
      ) */}
    </div>
  );
};

export default React.memo(MatchcenterPage);
